import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { category: "", text: "", options: ["", "", "", ""], correct_index: 0, points: 1, active: true };

export default function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/questions").then(({ data }) => setQuestions(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = [...new Set(questions.map((q) => q.category))];
  const shown = filter ? questions.filter((q) => q.category === filter) : questions;

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, category: filter || "" }); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({ category: q.category, text: q.text, options: [...q.options], correct_index: q.correct_index, points: q.points, active: q.active });
    setOpen(true);
  };

  const save = async () => {
    const opts = form.options.map((o) => o.trim()).filter(Boolean);
    if (opts.length < 2) { toast.error("Servono almeno 2 opzioni"); return; }
    try {
      const payload = { ...form, options: opts, correct_index: Math.min(form.correct_index, opts.length - 1), points: Number(form.points) || 1 };
      if (editing) {
        await api.put(`/admin/questions/${editing.id}`, payload);
        toast.success("Domanda aggiornata");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Domanda creata");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (q) => {
    try {
      await api.delete(`/admin/questions/${q.id}`);
      toast.success("Domanda eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2" data-testid="question-category-filters">
          <Button variant={filter === "" ? "default" : "outline"} size="sm" onClick={() => setFilter("")}>Tutte</Button>
          {categories.map((c) => (
            <Button key={c} data-testid={`filter-category-${c}`} variant={filter === c ? "default" : "outline"} size="sm" onClick={() => setFilter(c)}>{c}</Button>
          ))}
        </div>
        <Button data-testid="admin-add-question-btn" className="bg-[#003366] hover:bg-[#002244]" onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> Nuova domanda
        </Button>
      </div>

      <div className="space-y-3" data-testid="admin-questions-list">
        {shown.map((q) => (
          <Card key={q.id} className="institutional-card">
            <CardContent className="p-4 sm:p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge className="bg-[#E0E7FF] text-[#003366]">{q.category}</Badge>
                  <Badge variant="secondary">{q.points} pt</Badge>
                  {!q.active && <Badge className="bg-slate-100 text-slate-500">Disattivata</Badge>}
                </div>
                <p className="text-sm font-medium text-slate-900">{q.text}</p>
                <p className="text-xs text-green-700 mt-1">Corretta: {q.options[q.correct_index]}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button data-testid={`question-edit-${q.id}`} variant="ghost" size="sm" onClick={() => openEdit(q)}><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`question-delete-${q.id}`} variant="ghost" size="sm" onClick={() => remove(q)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {shown.length === 0 && <p className="text-sm text-slate-500">Nessuna domanda presente.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="question-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica domanda" : "Nuova domanda"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Categoria</Label>
              <Input data-testid="question-form-category" placeholder="es. Logica, Inglese Tecnico, Informatica..." value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} list="category-suggestions" />
              <datalist id="category-suggestions">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              <p className="text-xs text-slate-500 mt-1">
                Max 5 categorie. Le categorie con "inglese" nel nome sono saltate dai candidati con certificato di
                conoscenza della lingua inglese non scaduto, di livello B1 o superiore;
                quelle con "informatica"/"digit"/"ECDL" dai candidati con certificazione ECDL/ICDL (punteggio pieno automatico).
              </p>
            </div>
            <div>
              <Label>Testo della domanda</Label>
              <Input data-testid="question-form-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
            </div>
            <div>
              <Label className="mb-2 block">Opzioni (seleziona la risposta corretta)</Label>
              <RadioGroup value={String(form.correct_index)} onValueChange={(v) => setForm({ ...form, correct_index: parseInt(v, 10) })} className="space-y-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <RadioGroupItem data-testid={`question-form-correct-${i}`} value={String(i)} />
                    <Input data-testid={`question-form-option-${i}`} value={o} placeholder={`Opzione ${i + 1}`}
                      onChange={(e) => { const opts = [...form.options]; opts[i] = e.target.value; setForm({ ...form, options: opts }); }} />
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Label>Punti</Label>
                <Input data-testid="question-form-points" type="number" min={1} max={10} className="w-20" value={form.points}
                  onChange={(e) => setForm({ ...form, points: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="question-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label>Attiva</Label>
              </div>
            </div>
            <Button data-testid="question-form-save" className="w-full bg-[#003366] hover:bg-[#002244]"
              disabled={!form.category.trim() || !form.text.trim()} onClick={save}>
              Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
